import { computed, ref } from 'vue';
import type { Station } from '@/entities/station/station.entity';
import type { CustomLocations } from '@/constants/custom-locations.constant';
import type { User } from '@/entities/auth/user.entity';
import type { BookingLocation } from '@/entities/bookings/booking.entity';
import type { VehicleCategory } from '@/entities/vehicle-category.enum';
import { type InsuranceCase, type Offer, VehicleExtraType } from '@carvia/ros-client-types';
import { getAllCustomBookedExtras } from '@/utils/pricing.utils';
import { isCustomerPaidPartner, isLongTermDurationTooShort } from '@/utils/long-term-create.utils';
import type { Pricing } from '@/entities/pricing/pricing.entity';
import { DateTime } from 'luxon';

export type CreateBookingProps = ReturnType<typeof useCreateBookingProps>;

export const useCreateBookingProps = (initialValues?: {
  startDate?: Date;
  endDate?: Date;
  station?: Station | CustomLocations;
  pickupLocation?: BookingLocation;
  dropoffLocation?: BookingLocation;
  categoryFilter?: VehicleCategory;
  makeFilter?: string;
  selectedOffer?: Offer;
  insuranceCase?: InsuranceCase;
  partner?: User;
}) => {
  const initialStartDate = DateTime.now()
    .plus({ days: 1 })
    .set({ hour: 11, minute: 0, second: 0, millisecond: 0 });
  const startDate = ref(initialValues?.startDate ?? initialStartDate.toJSDate());
  const endDate = ref(initialValues?.endDate ?? initialStartDate.plus({ days: 2 }).toJSDate());
  const selectedOffer = ref<Offer | null>(initialValues?.selectedOffer ?? null);
  const pricing = ref<Pricing | null>(initialValues?.selectedOffer?.pricing ?? null);
  return {
    startDate,
    endDate,
    station: ref<Station | CustomLocations | null>(initialValues?.station ?? null),
    pickupLocation: ref<BookingLocation | null>(initialValues?.pickupLocation ?? null),
    dropoffLocation: ref<BookingLocation | null>(initialValues?.dropoffLocation ?? null),
    categoryFilter: ref<VehicleCategory | null>(initialValues?.categoryFilter ?? null),
    makeFilter: ref<string | null>(initialValues?.makeFilter ?? null),
    insuranceCase: ref(initialValues?.insuranceCase),
    insuranceCaseProvider: ref<'roland' | 'axa' | 'europ' | 'volvo' | 'jlr' | null>(null),
    selectedOffer,
    pricing,
    longTerm: ref(
      !!(
        !initialValues?.insuranceCase &&
        initialValues?.startDate &&
        initialValues?.endDate &&
        !isLongTermDurationTooShort(initialValues.startDate, initialValues.endDate) &&
        isCustomerPaidPartner(initialValues.partner)
      ),
    ),
    transferDeliveryPrice: computed(() =>
      pricing.value
        ? getAllCustomBookedExtras(pricing.value).find(
            (extra) => extra.customVehicleExtraType === VehicleExtraType.TRANSFER_PICKUP,
          )?.price
        : undefined,
    ),
    transferReturnPrice: computed(() =>
      pricing.value
        ? getAllCustomBookedExtras(pricing.value).find(
            (extra) => extra.customVehicleExtraType === VehicleExtraType.TRANSFER_RETURN,
          )?.price
        : undefined,
    ),
    partner: ref(initialValues?.partner),
    includeTransfers: ref(false),
  };
};
